// ---------------------------------------------------------------------------
// AppLock — Default DOM-based lock screen UI
// ---------------------------------------------------------------------------
import lockScreenCSS from './lock-screen-css.js';
// SVG icons (inline to avoid external dependencies)
const LOCK_ICON = `<svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
const EYE_ICON = `<svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;
const EYE_OFF_ICON = `<svg viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
/**
 * Self-contained lock screen that renders into the DOM.
 *
 * Features:
 * - Two modes: setup (first-time) and unlock
 * - Password visibility toggle
 * - Failed attempt feedback with progressive delay display
 * - Focus trap within the overlay
 * - Accessible: aria-live, focus management, keyboard operation
 * - Injects its own scoped CSS
 */
export class LockScreenUI {
    constructor(config, bus) {
        this.config = config;
        this.bus = bus;
        this.overlay = null;
        this.styleElement = null;
        this.isSetupMode = false;
        this.passwordVisible = false;
        this.failedAttempts = 0;
        this.delayUntil = 0;
    }
    // -----------------------------------------------------------------------
    // Public
    // -----------------------------------------------------------------------
    /**
     * Show the lock screen in unlock mode.
     */
    show(onSubmit) {
        this.isSetupMode = false;
        this.render(onSubmit, undefined);
    }
    /**
     * Show the lock screen in first-time setup mode.
     */
    showSetup(onSetup) {
        this.isSetupMode = true;
        this.render(undefined, onSetup);
    }
    /**
     * Remove the lock screen from the DOM.
     */
    hide() {
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
        this.showAppRoot();
        this.failedAttempts = 0;
        this.delayUntil = 0;
    }
    /**
     * Clean up: remove DOM elements and injected styles.
     */
    destroy() {
        this.hide();
        if (this.styleElement) {
            this.styleElement.remove();
            this.styleElement = null;
        }
    }
    // -----------------------------------------------------------------------
    // Internal: Rendering
    // -----------------------------------------------------------------------
    render(onSubmit, onSetup) {
        this.injectStyles();
        this.hideAppRoot();
        // Remove any existing overlay
        if (this.overlay) {
            this.overlay.remove();
        }
        const overlay = document.createElement('div');
        overlay.className = `applock-overlay ${this.config.lockScreenClass}`.trim();
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', this.isSetupMode ? 'Set up app lock' : 'App locked');
        if (this.isSetupMode) {
            overlay.innerHTML = this.setupTemplate();
        }
        else {
            overlay.innerHTML = this.unlockTemplate();
        }
        const mount = this.config.mountPoint ?? document.body;
        mount.appendChild(overlay);
        this.overlay = overlay;
        // Wire up event handlers
        if (this.isSetupMode) {
            this.wireSetupEvents(overlay, onSetup);
        }
        else {
            this.wireUnlockEvents(overlay, onSubmit);
        }
        // Focus the first input
        const input = overlay.querySelector('.applock-input');
        if (input) {
            requestAnimationFrame(() => input.focus());
        }
        // Set up focus trap
        this.trapFocus(overlay);
    }
    // -----------------------------------------------------------------------
    // Templates
    // -----------------------------------------------------------------------
    unlockTemplate() {
        return `
      <div class="applock-card">
        <div class="applock-icon">${LOCK_ICON}</div>
        <h2 class="applock-title">App Locked</h2>
        <p class="applock-subtitle">Enter your password to continue</p>
        <form id="applock-unlock-form" autocomplete="off">
          <div class="applock-input-group">
            <input
              type="password"
              class="applock-input"
              id="applock-password"
              placeholder="Password"
              autocomplete="current-password"
              aria-label="Password"
            />
            <button type="button" class="applock-toggle-vis" aria-label="Toggle password visibility">
              ${EYE_ICON}
            </button>
          </div>
          <button type="submit" class="applock-submit" id="applock-submit">Unlock</button>
        </form>
        <div class="applock-feedback" id="applock-feedback" aria-live="polite"></div>
      </div>
    `;
    }
    setupTemplate() {
        const isEncrypted = this.config.mode === 'encrypted';
        const warningHTML = isEncrypted ? `
      <div class="applock-warning-box">
        <strong>⚠ No password recovery</strong>
        Your data will be encrypted locally. If you forget your password,
        your data cannot be recovered. There is no server, no reset email,
        and no backdoor.
      </div>
    ` : '';
        return `
      <div class="applock-card">
        <div class="applock-icon">${LOCK_ICON}</div>
        <h2 class="applock-title">Set Up App Lock</h2>
        <p class="applock-subtitle">
          Create a password to ${isEncrypted ? 'encrypt and ' : ''}protect your data
        </p>
        ${warningHTML}
        <form id="applock-setup-form" autocomplete="off">
          <div class="applock-input-group">
            <input
              type="password"
              class="applock-input"
              id="applock-password-new"
              placeholder="Enter password"
              autocomplete="new-password"
              aria-label="New password"
            />
            <button type="button" class="applock-toggle-vis" aria-label="Toggle password visibility">
              ${EYE_ICON}
            </button>
          </div>
          <div class="applock-input-group">
            <input
              type="password"
              class="applock-input"
              id="applock-password-confirm"
              placeholder="Confirm password"
              autocomplete="new-password"
              aria-label="Confirm password"
            />
          </div>
          <button type="submit" class="applock-submit" id="applock-submit-setup">
            ${isEncrypted ? 'Enable Encryption' : 'Enable Lock'}
          </button>
        </form>
        <div class="applock-feedback" id="applock-feedback" aria-live="polite"></div>
      </div>
    `;
    }
    // -----------------------------------------------------------------------
    // Event wiring
    // -----------------------------------------------------------------------
    wireUnlockEvents(overlay, onSubmit) {
        const form = overlay.querySelector('#applock-unlock-form');
        const input = overlay.querySelector('#applock-password');
        const feedback = overlay.querySelector('#applock-feedback');
        const toggleBtn = overlay.querySelector('.applock-toggle-vis');
        // Toggle password visibility
        toggleBtn.addEventListener('click', () => {
            this.passwordVisible = !this.passwordVisible;
            input.type = this.passwordVisible ? 'text' : 'password';
            toggleBtn.innerHTML = this.passwordVisible ? EYE_OFF_ICON : EYE_ICON;
        });
        // Form submission
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const password = input.value;
            if (!password)
                return;
            // Check progressive delay
            const now = Date.now();
            if (now < this.delayUntil) {
                const remaining = Math.ceil((this.delayUntil - now) / 1000);
                feedback.className = 'applock-feedback applock-feedback--delay';
                feedback.textContent = `Too many attempts. Try again in ${remaining}s.`;
                return;
            }
            const submitBtn = overlay.querySelector('.applock-submit');
            submitBtn.disabled = true;
            submitBtn.textContent = 'Verifying…';
            input.classList.remove('applock-input--error');
            const success = await onSubmit(password);
            if (!success) {
                this.failedAttempts++;
                input.classList.add('applock-input--error');
                input.value = '';
                input.focus();
                if (this.failedAttempts >= this.config.maxAttempts) {
                    const delaySec = Math.min(Math.pow(2, this.failedAttempts - this.config.maxAttempts), 30);
                    this.delayUntil = Date.now() + delaySec * 1000;
                    feedback.className = 'applock-feedback applock-feedback--delay';
                    feedback.textContent = `Incorrect password. Wait ${delaySec}s before trying again.`;
                }
                else {
                    feedback.className = 'applock-feedback applock-feedback--error';
                    feedback.textContent = `Incorrect password. ${this.config.maxAttempts - this.failedAttempts} attempts remaining.`;
                }
                this.bus.emit('unlock:fail', { attempts: this.failedAttempts });
            }
            submitBtn.disabled = false;
            submitBtn.textContent = 'Unlock';
        });
    }
    wireSetupEvents(overlay, onSetup) {
        const form = overlay.querySelector('#applock-setup-form');
        const inputNew = overlay.querySelector('#applock-password-new');
        const inputConfirm = overlay.querySelector('#applock-password-confirm');
        const feedback = overlay.querySelector('#applock-feedback');
        const toggleBtn = overlay.querySelector('.applock-toggle-vis');
        toggleBtn.addEventListener('click', () => {
            this.passwordVisible = !this.passwordVisible;
            inputNew.type = this.passwordVisible ? 'text' : 'password';
            inputConfirm.type = this.passwordVisible ? 'text' : 'password';
            toggleBtn.innerHTML = this.passwordVisible ? EYE_OFF_ICON : EYE_ICON;
        });
        form.addEventListener('submit', async (e) => {
            e.preventDefault();
            const password = inputNew.value;
            const confirm = inputConfirm.value;
            if (!password) {
                feedback.className = 'applock-feedback applock-feedback--error';
                feedback.textContent = 'Please enter a password.';
                return;
            }
            if (password.length < 4) {
                feedback.className = 'applock-feedback applock-feedback--error';
                feedback.textContent = 'Password must be at least 4 characters.';
                return;
            }
            if (password !== confirm) {
                feedback.className = 'applock-feedback applock-feedback--error';
                feedback.textContent = 'Passwords do not match.';
                inputConfirm.classList.add('applock-input--error');
                inputConfirm.value = '';
                inputConfirm.focus();
                return;
            }
            const submitBtn = overlay.querySelector('.applock-submit');
            submitBtn.disabled = true;
            submitBtn.textContent = 'Setting up…';
            try {
                await onSetup(password);
            }
            catch (err) {
                feedback.className = 'applock-feedback applock-feedback--error';
                feedback.textContent = `Setup failed: ${err.message}`;
                submitBtn.disabled = false;
                submitBtn.textContent = this.config.mode === 'encrypted' ? 'Enable Encryption' : 'Enable Lock';
            }
        });
    }
    // -----------------------------------------------------------------------
    // DOM helpers
    // -----------------------------------------------------------------------
    injectStyles() {
        if (this.styleElement)
            return;
        this.styleElement = document.createElement('style');
        this.styleElement.setAttribute('data-applock', '');
        this.styleElement.textContent = lockScreenCSS;
        document.head.appendChild(this.styleElement);
    }
    hideAppRoot() {
        const root = document.querySelector(this.config.appRootSelector);
        if (root) {
            root.style.display = 'none';
        }
    }
    showAppRoot() {
        const root = document.querySelector(this.config.appRootSelector);
        if (root) {
            root.style.display = '';
        }
    }
    /**
     * Trap focus within the overlay so Tab/Shift+Tab cycle
     * only through lock screen elements.
     */
    trapFocus(overlay) {
        overlay.addEventListener('keydown', (e) => {
            if (e.key !== 'Tab')
                return;
            const focusable = overlay.querySelectorAll('input, button, [tabindex]:not([tabindex="-1"])');
            if (focusable.length === 0)
                return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        });
    }
}
//# sourceMappingURL=lock-screen.js.map